import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
  BoltOutlined as BoltIcon,
  PictureAsPdfOutlined as PdfIcon,
  SmartphoneOutlined as MobileIcon,
  FactCheckOutlined as ApproveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';

const PROMO_KEY = 'inspectiq_platform_promo_seen';

const features = [
  { icon: <MobileIcon />, text: 'Offline-first mobile field capture' },
  { icon: <PdfIcon />, text: 'Automated PDF reports in < 3 seconds' },
  { icon: <ApproveIcon />, text: 'Manager approval workflow' },
  { icon: <BoltIcon />, text: '~90% less report-writing time' },
];

const PlatformPromo = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const seen = localStorage.getItem(PROMO_KEY);
      if (!seen) {
        const timer = setTimeout(() => setOpen(true), 2500);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      // localStorage unavailable
    }
  }, []);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(PROMO_KEY, Date.now().toString()); } catch (e) {}
  };

  const goToPlatform = () => {
    trackEvent('promo_click', { promo: 'platform_overlay', action: 'go_to_platform' });
    dismiss();
    navigate('/platform');
  };

  const handleClose = () => {
    trackEvent('promo_click', { promo: 'platform_overlay', action: 'dismiss' });
    dismiss();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #012637 0%, #01567a 100%)',
          color: 'white',
          p: { xs: 3, sm: 4 },
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>

        <Chip
          label="NEW"
          size="small"
          sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 700, mb: 2 }}
        />
        <Typography
          sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 800,
            fontSize: { xs: '1.5rem', sm: '1.85rem' },
            lineHeight: 1.2,
            mb: 1.5,
          }}
        >
          Digitize your inspection operations
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', maxWidth: 400 }}>
          InspectIQ Platform automates the entire inspection lifecycle — from field
          capture to branded report delivery — on one connected system.
        </Typography>
      </Box>

      {/* Features */}
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          {features.map((f, i) => (
            <Stack key={i} direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(1,86,122,0.08)',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                {f.text}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            endIcon={<ArrowIcon />}
            onClick={goToPlatform}
            sx={{ py: 1.5 }}
          >
            Explore the Platform
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleClose}
            sx={{ py: 1.5 }}
          >
            Maybe later
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          Built for inspection agencies worldwide
        </Typography>
      </Box>
    </Dialog>
  );
};

export default PlatformPromo;
